import React, { useState, useContext, useEffect, useRef, useCallback } from "react";
import {
  LoadingOverlay,
  Loader,
  Text,
  Button,
  Group,
  Stack,
  Center,
  ThemeIcon,
  Box,
  useMantineTheme,
} from "@mantine/core";
import { useForm } from "@mantine/form";
import { IconShieldCheck, IconPhoneCheck } from "@tabler/icons-react";
import { DataContext } from "../../context/DataContext";
import { isMobile } from "react-device-detect";
import { getEdgeQueueUrl, getEdgeRetrieveUrl } from "../../utils/edge";
import useStatusCheck from "../../hooks/useStatusCheck";
import useEdgeStatusCheck from "../../hooks/useEdgeStatusCheck";
import { useMediaQuery } from "@mantine/hooks";
import useAdjustInputSize from "../../hooks/useAdjustInputSize";
import useMessagingDisclaimer from "../../hooks/useMessagingDisclaimer";
import FloatingLabelInput from "../../components/floating-label-input";
import DateOfBirthSelect from "../../components/date-of-birth-select";
import AddressInput from "../../components/address-input";
import UkAddressInput from "../../components/uk-address-input";
import DeAddressInput from "../../components/de-address-input";
import {
  getAddressRegion,
  isDayFirstRegion,
  ADDRESS_REGION_UK,
  ADDRESS_REGION_DE,
} from "../../utils/addressRegion";
import OtpInput from "../../components/otp-input";
import VerificationProcessingScreen from "../../components/verification-processing-screen";
export default function PhoneMatch() {
  const { data, dispatch, text, routeSequence, currentIndex, navigateToNext, settings } = useContext(DataContext);
  const theme = useMantineTheme();
  const messagingDisclaimer = useMessagingDisclaimer();

  // Edge mode detection
  const isEdgeMode = data?.edge === true;
  const [isVerifying, setIsVerifying] = useState(false);
  console.log('[PhoneMatch Page] Edge mode check:', { isEdgeMode, dataEdge: data?.edge });

  const [loading, setLoading] = useState(false);
  const [isOtpInput, setIsOtpInput] = useState(false);
  const [showOtpScreen, setShowOtpScreen] = useState(false);
  const [sendingCode, setSendingCode] = useState(false);
  const [template, setTemplate] = useState({});
  const [result, setResult] = useState(false);
  const [hasHandledFailure, setHasHandledFailure] = useState(false);
  const [edgeStatusPolling, setEdgeStatusPolling] = useState(false);
  // Terminal status captured for VerificationProcessingScreen.
  // "PASS" comes from polling; "FAIL_EXHAUSTED" from the inline
  // synchronous failure path. handleProcessingComplete dispatches the
  // appropriate terminal action once the screen plays through.
  const [terminalStatus, setTerminalStatus] = useState(null);
  const smallScreen = useMediaQuery("(max-width: 40em)");
  const isIframe = window !== window.top;
  const inputSize = useAdjustInputSize();
  const firstNameInputRef = useRef(null);
  const otpInputRef = useRef(null);

  const [activeStep, setActiveStep] = useState(0);
  const [collectDob, setCollectDob] = useState(true);
  const [collectAddress, setCollectAddress] = useState(false);
  const [collectSsn, setCollectSsn] = useState(false);
  const [debugInfo, setDebugInfo] = useState({});
  const currentPath = "phonematch";

  const hasAdditionalFields = collectAddress || collectSsn;

  // When nameAndPhoneOnly, hide name fields if data context already has values
  const nameAndPhoneOnly = settings?.journey?.route?.find(
    (route) => route.path === currentPath
  )?.nameAndPhoneOnly === true;
  const hideNameFields = nameAndPhoneOnly && data.fn && data.fn.length >= 2 && data.ln && data.ln.length >= 2;

  useEffect(() => {
    const matchedRoute = settings?.journey?.route?.find(
      (route) => route.path === currentPath
    );
    const inputs = matchedRoute?.inputs || [];
    const addressRegion = getAddressRegion(settings, currentPath);
    const isUKAddressSetting = addressRegion === ADDRESS_REGION_UK;
    const isDeAddressSetting = addressRegion === ADDRESS_REGION_DE;
    // Only collect address if explicitly in inputs array
    const addressNeeded = inputs.includes("address") || inputs.includes("fulladdress") || inputs.includes("residence");
    const ssnNeeded = inputs.includes("ssn") || inputs.includes("last4ssn") || inputs.includes("lastfourssn") || inputs.includes("ssn4");
    const nameAndPhoneOnly = matchedRoute?.nameAndPhoneOnly === true;
    setCollectDob(!nameAndPhoneOnly);
    setCollectAddress(addressNeeded);
    setCollectSsn(ssnNeeded);
    setDebugInfo({
      isUKAddressSetting,
      isDeAddressSetting,
      addressRegion,
      inputs,
      addressNeeded,
      ssnNeeded,
      nameAndPhoneOnly,
      journeySettings: settings?.journey?.settings,
      matchedRoute,
    });
  }, [settings, currentPath]);

  // Re-focus OTP input when transitioning to OTP screen
  // This is a safety net — primary focus happens in submitHandler's gesture chain
  useEffect(() => {
    if ((showOtpScreen || isOtpInput) && !loading && otpInputRef.current) {
      otpInputRef.current.focus();
    }
  }, [showOtpScreen, isOtpInput, loading]);

  // Check if this component is skippable based on route settings
  const currentRoute = routeSequence[currentIndex];
  const isSkippable = currentRoute?.skipable === true;

  // Determine which fields should be disabled from route configuration
  const disabledFields = Array.isArray(currentRoute?.component?.disabled)
    ? currentRoute.component.disabled
    : Array.isArray(currentRoute?.disabled)
    ? currentRoute.disabled
    : [];

  const getNewPin = () => {
    setIsOtpInput(false);
    setShowOtpScreen(false);
    setSendingCode(false);
    setTemplate({});
    setResult(false);
    // Reset the in-flight verification state too — without these,
    // returning to the phone form leaves the submit button stuck in
    // its "Sending code..." disabled state and the screen-takeover
    // conditions still satisfied.
    setIsVerifying(false);
    setLoading(false);
    setEdgeStatusPolling(false);
    setTerminalStatus(null);
    setHasHandledFailure(false);
    form.setFieldValue("pin", "");
    // Clear the OTP continuation payload from the shared `data`
    // object too — submitHandler mutates `data` directly when the
    // user enters an OTP, and on re-submit the backend would
    // otherwise see the stale phonematch_continue (with the prior
    // OTP key) and skip straight to a terminal FAIL without
    // re-issuing a new code.
    delete data.phonematch_continue;
    data.isOtpInput = false;
  };

  // Handler for skip button
  const handleSkip = () => {
    // Update session state to mark phonematch as complete
    const updatedSessionState = {
      ...data.sessionState,
      phonematch: "COMPLETED"
    };

    // Update the session state
    dispatch({
      type: "SET_SESSION_STATE",
      payload: updatedSessionState
    });

    // Mark current component as complete to allow navigation
    dispatch({
      type: "SET_IS_CURRENT_COMPONENT_COMPLETE",
      payload: true
    });

    // Trigger navigation to the next step
    dispatch({ type: "TRIGGER_NAVIGATION" });
  };

  const captureTerminal = useCallback((statusResult) => {
    if (!statusResult) return true;
    const status = statusResult.status;
    const processState = statusResult.process_state;
    const action = statusResult.action;
    // process_state: "NEXT_APP_TRIGGER" is the journey engine's
    // "this app is done, advance to the next route" signal — it's
    // terminal for THIS component even though the overall session
    // status stays PENDING. handleProcessingComplete picks it apart.
    if (processState === "NEXT_APP_TRIGGER") {
      setTerminalStatus("NEXT_APP");
      return false;
    }
    // Out-of-band (OTP) FAIL: the worker reports transaction_state
    // "awaiting_user" with the FAIL only on latest_event.action — the
    // top-level status stays PENDING, so the generic checks below never
    // match and this handler polls forever. A phonematch fail falls
    // through to the next verification step immediately (no in-step
    // retries): NEXT_APP semantics — session stays PENDING and the next
    // route owns the result.
    if (
      statusResult.transaction_state === "awaiting_user" &&
      ["FAIL", "REVIEW"].includes(
        statusResult.latest_event?.action?.toUpperCase()
      )
    ) {
      setTerminalStatus("NEXT_APP");
      return false;
    }
    if (processState === "COMPLETED") {
      setTerminalStatus(status === "PASS" ? "PASS" : "FAIL");
      return false;
    }
    if (status === "PASS" || action === "PASS") {
      setTerminalStatus("PASS");
      return false;
    }
    if (status === "FAIL" || action === "FAIL" || action === "ERROR") {
      setTerminalStatus("FAIL");
      return false;
    }
    return true;
  }, []);

  const handleProcessingComplete = useCallback(() => {
    if (terminalStatus === "PASS") {
      dispatch({ type: "SET_STATUS", payload: "PASS" });
      dispatch({ type: "SET_IS_CURRENT_COMPONENT_COMPLETE", payload: true });
    } else if (terminalStatus === "NEXT_APP") {
      // Journey advances to the next app — session status stays
      // PENDING, the next route owns the result.
      dispatch({ type: "SET_IS_CURRENT_COMPONENT_COMPLETE", payload: true });
      dispatch({ type: "TRIGGER_NAVIGATION" });
    } else {
      // FAIL or FAIL_EXHAUSTED — same downstream pipeline as before.
      dispatch({ type: "SET_IS_CURRENT_COMPONENT_COMPLETE", payload: true });
      if (terminalStatus === "FAIL") {
        dispatch({ type: "SET_STATUS", payload: "FAIL" });
      }
    }
  }, [terminalStatus, dispatch]);

  useStatusCheck(!isEdgeMode && result, false, "phonematch", captureTerminal);
  useEdgeStatusCheck(isEdgeMode && edgeStatusPolling, captureTerminal);

  const form = useForm({
    initialValues: {
      fn: data.fn || "",
      ln: data.ln || "",
      phone: data.phone || "",
      dob: data.dob || "",
      addr: data.addr || "",
      city: data.city || "",
      state: data.state || "",
      zip: data.zip || "",
      ssn: data.ssn || "",
      pin: "",
    },
    validate: (values) => {
      if (isOtpInput) {
        return {
          pin: values.pin.length < 6 ? text.phonematch.code.error : null,
        };
      }
      const errors = {};

      if (!disabledFields.includes("phone")) {
        const phoneDigits = values.phone.replace(/\D/g, "");
        const phoneValid =
          phoneDigits.length === 10 ||
          (phoneDigits.length === 11 && phoneDigits.startsWith("1"));
        if (!phoneValid) {
          errors.phone = text.general.phoneError;
        }
      }

      if (!hideNameFields && !disabledFields.includes("fn") && values.fn.length < 2) {
        errors.fn = text.general.firstNameError;
      }

      if (!hideNameFields && !disabledFields.includes("ln") && values.ln.length < 2) {
        errors.ln = text.general.lastNameError;
      }

      if (collectDob && activeStep === 0 && !disabledFields.includes("dob")) {
        const dobPattern =
          /^(19|20)\d{2}(0[1-9]|1[0-2])(0[1-9]|[12][0-9]|3[01])$/;
        const isDobFormatCorrect = dobPattern.test(values.dob);
        let dobError = text.general.dateOfBirthError;

        if (isDobFormatCorrect) {
          const year = parseInt(values.dob.substring(0, 4), 10);
          const month = parseInt(values.dob.substring(4, 6), 10) - 1;
          const day = parseInt(values.dob.substring(6, 8), 10);
          const date = new Date(year, month, day);
          if (
            date.getFullYear() === year &&
            date.getMonth() === month &&
            date.getDate() === day
          ) {
            dobError = null;
          }
        }
        errors.dob = dobError;
      }

      if (collectAddress && activeStep === 1 && !disabledFields.includes("addr")) {
        if (
          !values.addr ||
          !values.city ||
          !values.state ||
          values.zip.trim().length !== 5
        ) {
          errors.addr = "Address is required";
        } else {
          errors.addr = null;
        }
      }

      if (collectSsn && activeStep === 1 && !disabledFields.includes("ssn")) {
        if (/[^0-9]/.test(values.ssn) || values.ssn.length !== 4) {
          errors.ssn = text.general.lastFourSSNError;
        } else {
          errors.ssn = null;
        }
      }

      return errors;
    },
  });

  const handleDobChange = (dob) => {
    form.setFieldValue("dob", dob.year + dob.month + dob.day);
  };

  const handleAddressChange = (address) => {
    form.setFieldValue("addr", address.addr);
    form.setFieldValue("city", address.city);
    form.setFieldValue("state", address.state);
    form.setFieldValue("zip", address.zip);
  };

  const AddressComponent = debugInfo.isDeAddressSetting
    ? DeAddressInput
    : debugInfo.isUKAddressSetting
      ? UkAddressInput
      : AddressInput;

  const handleNext = () => {
    const validation = form.validate();
    if (
      !validation.errors.fn &&
      !validation.errors.ln &&
      !validation.errors.phone &&
      (!collectDob || !validation.errors.dob)
    ) {
      setActiveStep(1);
      form.clearErrors();
    }
  };

  // Edge mode: poll retrieve endpoint for result after initial submit
  const pollForResult = async (requestId) => {
    const maxAttempts = 100; // ~5 minutes
    const pollInterval = 3000; // 3 seconds
    let attempts = 0;

    const poll = async () => {
      try {
        const response = await fetch(getEdgeRetrieveUrl(), {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            request_id: requestId,
            type: "phonematch",
          }),
        });

        if (response.ok) {
          const result = await response.json();
          console.log('[PhoneMatch Page] Retrieve poll result:', result);

          // Check for final status (PASS/FAIL/ERROR) — no OTP needed
          if (result.action === "PASS" || result.action === "FAIL" || result.action === "ERROR" || result.status === "FAIL") {
            setEdgeStatusPolling(true);
            return; // Stop polling, edge status check handles the rest
          }

          // Check if OTP is needed (PENDING)
          if (result.action === "PENDING") {
            // Build the continuation template from the retrieve response
            const continueTemplate = result.continuation_template || {
              codematch: {
                key: "",
                confirmation: result.confirmation,
                token: result.token,
              }
            };
            setTemplate(continueTemplate);
            setIsOtpInput(true);
            setSendingCode(false);
            setLoading(false);
            return; // Stop polling, show OTP input
          }
        }

        attempts++;
        if (attempts < maxAttempts) {
          setTimeout(poll, pollInterval);
        } else {
          console.error("PhoneMatch polling timeout: No result received");
          setLoading(false);
        }
      } catch (error) {
        console.error("PhoneMatch polling error:", error);
        attempts++;
        if (attempts < maxAttempts) {
          setTimeout(poll, pollInterval);
        } else {
          setLoading(false);
        }
      }
    };

    poll();
  };

  // pinOverride lets the OTP auto-submit pass the just-typed value
  // through directly, avoiding React's async form-state propagation
  // (form.values.pin would otherwise be one render cycle behind).
  const submitHandler = (pinOverride = null) => {
    setIsVerifying(true);

    // Skip form.validate() for the auto-submit path — OtpInput
    // already guarantees the 6-digit constraint, and validate()
    // reads stale form.values.pin in this synchronous-fire window.
    if (!pinOverride) {
      const errors = form.validate();
      if (errors.hasErrors) {
        setIsVerifying(false);
        return;
      }
    }

    // For initial submit (not OTP), show the OTP screen optimistically
    // and focus the input within the user gesture chain so iOS keyboard opens
    if (!isOtpInput) {
      setShowOtpScreen(true);
      setSendingCode(true);
      if (otpInputRef.current) {
        otpInputRef.current.focus();
      }
    } else {
      setLoading(true);
    }

    {
      setHasHandledFailure(false); // Reset failure handling state on new request
      if (!disabledFields.includes("phone")) {
        data.phone = form.values.phone;
      }
      if (!disabledFields.includes("fn")) {
        data.fn = form.values.fn;
      }
      if (!disabledFields.includes("ln")) {
        data.ln = form.values.ln;
      }
      if (collectDob && !disabledFields.includes("dob")) {
        data.dob = form.values.dob;
      }
      if (collectAddress && !disabledFields.includes("addr")) {
        data.addr = form.values.addr;
        data.city = form.values.city;
        data.state = form.values.state;
        data.zip = form.values.zip;
      }
      if (collectSsn && !disabledFields.includes("ssn")) {
        data.ssn = form.values.ssn;
      }
      data.isOtpInput = isOtpInput;
      if (isOtpInput) {
        template.codematch.key = pinOverride || form.values.pin;
        data.phonematch_continue = template;
      }

      // Edge mode OTP submission goes to the queue publisher
      if (isEdgeMode && isOtpInput) {
        const reqId = settings.request_id;
        console.log('[PhoneMatch Page] Submitting OTP to publisher:', { reqId, code: form.values.pin });

        const otpData = { ...data, queue: "phonematch" };

        fetch(getEdgeQueueUrl(), {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(otpData),
        })
          .then((res) => res.json())
          .then((result) => {
            console.log('[PhoneMatch Page] OTP publisher result:', result);
            // After OTP is queued, start edge status polling for final PASS/FAIL
            setEdgeStatusPolling(true);
          })
          .catch((error) => {
            console.error("PhoneMatch OTP submission error:", error);
            setLoading(false);
          });
        return;
      }

      // Determine URL and request data based on edge mode
      const apiUrl = isEdgeMode
        ? getEdgeQueueUrl()
        : process.env.REACT_APP_BACKEND_URL + "/v3/stable/api/frontend/phonematch";

      const requestData = isEdgeMode
        ? { ...data, queue: "phonematch" }
        : data;

      console.log('[PhoneMatch Page] Submitting to:', apiUrl, { isEdgeMode });

      fetch(apiUrl, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify(requestData),
        }
      )
        .then(function (res) {
          return res.json();
        })
        .then(function (responseData) {
          if (isEdgeMode) {
            // Edge mode: queue accepted, now poll retrieve for the result
            const reqId = responseData.request_id || settings.request_id;
            if (responseData.request_id) {
              dispatch({ type: "SET_SETTINGS", payload: { ...settings, request_id: responseData.request_id } });
            }

            // Initial submit, poll for result (may return OTP template or direct result)
            if (reqId) {
              pollForResult(reqId);
            } else {
              console.error("No request_id found in edge response");
              setLoading(false);
            }
            return;
          }

          // Non-edge mode: handle synchronous response
          if (responseData.result.action === "PENDING" && !isOtpInput) {
            setTemplate(responseData.continuations.outofband.template);
            setIsOtpInput(true);
            setSendingCode(false);
            setLoading(false);
          } else if (responseData.result.action === "PASS") {
            setLoading(true);
            setResult(true);
          } else {
            // For failed results, keep loading state true to prevent form from showing
            if (!hasHandledFailure) {
              setResult(true);
              setHasHandledFailure(true);
              // Route through the processing screen — onComplete will
              // dispatch SET_IS_CURRENT_COMPONENT_COMPLETE so the
              // "Completing verification…" hold plays first.
              setTerminalStatus("FAIL_EXHAUSTED");
            }
          }
        })
        .catch(function () {
          alert("Something Went Wrong! Please try again.");
          setIsVerifying(false);
          setShowOtpScreen(false);
          setSendingCode(false);
        });
    }
  };

  useEffect(() => {
    setLoading(false);
    form.clearErrors();
  }, [hasAdditionalFields]);

  // Only the post-OTP polling phase hands off to the staged
  // verification processing screen. In non-edge mode `result` flips
  // true once the OTP response lands; in edge mode `edgeStatusPolling`
  // flips true after the OTP has been queued. Both signal "OTP
  // submitted, now waiting on the final status". The initial phone
  // submit and the OTP submit both stay on the form during their
  // brief in-flight window so the inline button-spinner UX is
  // preserved and the OTP auto-submit keeps working.
  if (result || edgeStatusPolling) {
    return (
      <VerificationProcessingScreen
        type="datamatch"
        resultReady={terminalStatus !== null}
        onComplete={handleProcessingComplete}
      />
    );
  }

  return (
    <>
      <style>{`@keyframes spin { 0% { transform: rotate(0deg); } 100% { transform: rotate(360deg); } }`}</style>
      <form
        onSubmit={form.onSubmit(submitHandler)}
        style={{
          display: "flex",
          flexDirection: "column",
          justifyContent: "space-between", // This will push the button to the bottom
          height: "100%", // This should take the full height of its parent
        }}
      >
        <Box>
          {!showOtpScreen && !isOtpInput && (
            <>
              <Stack spacing={6} mt={inputSize === "md" ? 5 : 5} align="flex-start">
                <Group spacing="sm" align="center" noWrap>
                  <ThemeIcon
                    variant="light"
                    c="teal"
                    size={inputSize === "md" ? 44 : 40}
                    radius={"xl"}
                  >
                    <IconPhoneCheck
                      size={inputSize === "md" ? 26 : 24}
                      stroke={1.5}
                      color="teal"
                    />
                  </ThemeIcon>
                  <Text fz={inputSize === "md" ? 24 : 20} fw={700}>
                    {text.phonematch.title}
                  </Text>
                </Group>

                <Text c="dimmed" fz={inputSize}>
                  {(() => {
                    const subtitle = (text.phonematch.subTitle || "").trim();
                    if (!messagingDisclaimer) return subtitle;
                    // Append a period if the subtitle doesn't already
                    // end with one so the disclaimer reads as its own
                    // sentence rather than running into the previous.
                    const sep = /[.!?]$/.test(subtitle) ? " " : ". ";
                    return `${subtitle}${sep}${messagingDisclaimer}`;
                  })()}
                </Text>
              </Stack>

              {hasAdditionalFields ? (
                <Stack mt={20} spacing={"md"}>
                  {activeStep === 0 && (
                    <>
                      {!hideNameFields && (
                        <>
                          <Box>
                            <FloatingLabelInput
                              size={"lg"}
                              label={text.general.firstName}
                              placeholder={text.general.firstNamePlaceholder}
                              {...form.getInputProps("fn")}
                              disabled={disabledFields.includes("fn")}
                            />
                          </Box>
                          <Box>
                            <FloatingLabelInput
                              size={"lg"}
                              label={text.general.lastName}
                              {...form.getInputProps("ln")}
                              disabled={disabledFields.includes("ln")}
                            />
                          </Box>
                        </>
                      )}
                      {collectDob && (
                        <Box>
                          <DateOfBirthSelect
                          dayFirst={isDayFirstRegion(debugInfo.addressRegion)}
                            isSinglePageForm={true}
                            onDobChange={handleDobChange}
                            {...form.getInputProps("dob")}
                            disabled={disabledFields.includes("dob")}
                          />
                        </Box>
                      )}
                      <Box>
                        <FloatingLabelInput
                          size={"lg"}
                          label={text.general.phone}
                          tooltip={text.general.phoneTooltip}
                          placeholder={text.general.phonePlaceholder}
                          inputMode="tel"
                          {...form.getInputProps("phone")}
                          disabled={disabledFields.includes("phone")}
                          customOnChange={(event) => {
                            let digits = (event.target.value || "").replace(
                              /\D/g,
                              ""
                            );
                            if (
                              !(digits.length === 11 && digits.startsWith("1"))
                            ) {
                              digits = digits.substring(0, 10);
                            }
                            const value =
                              digits.length === 11
                                ? digits.replace(
                                    /^1(\d{3})(\d{3})(\d{4})$/,
                                    "1 $1-$2-$3"
                                  )
                                : digits.replace(
                                    /(\d{3})(\d{3})(\d{4})/,
                                    "$1-$2-$3"
                                  );
                            form.getInputProps("phone").onChange(value);
                            form.setFieldValue("phone", value);
                          }}
                        />
                      </Box>
                    </>
                  )}
                  {activeStep === 1 && (
                    <>
                      {collectAddress && (
                        <Box>
                          <AddressComponent
                            onAddressChange={handleAddressChange}
                            {...form.getInputProps("addr")}
                            value={{
                              addr: form.values.addr,
                              city: form.values.city,
                              state: form.values.state,
                              zip: form.values.zip,
                            }}
                            error={form.errors.addr}
                          />
                        </Box>
                      )}
                      {collectSsn && (
                        <Box>
                          <FloatingLabelInput
                            size={"lg"}
                            label={text.general.lastFourSSN}
                            isMaskedInput
                            maxLength={4}
                            inputMode="numeric"
                            pattern="[0-9]*"
                            {...form.getInputProps("ssn")}
                            disabled={disabledFields.includes("ssn")}
                          />
                        </Box>
                      )}
                    </>
                  )}
                </Stack>
              ) : (
                <Stack mt={20} spacing={"md"}>
                  {!hideNameFields && (
                    <>
                      <Box>
                        <FloatingLabelInput
                          size={"lg"}
                          label={text.general.firstName}
                          placeholder={text.general.firstNamePlaceholder}
                          {...form.getInputProps("fn")}
                          disabled={disabledFields.includes("fn")}
                        />
                      </Box>
                      <Box>
                        <FloatingLabelInput
                          size={"lg"}
                          label={text.general.lastName}
                          {...form.getInputProps("ln")}
                          disabled={disabledFields.includes("ln")}
                        />
                      </Box>
                    </>
                  )}
                  {collectDob && (
                    <Box>
                      <DateOfBirthSelect
                          dayFirst={isDayFirstRegion(debugInfo.addressRegion)}
                        isSinglePageForm={true}
                        onDobChange={handleDobChange}
                        {...form.getInputProps("dob")}
                        disabled={disabledFields.includes("dob")}
                      />
                    </Box>
                  )}
                  <Box>
                    <FloatingLabelInput
                      size={"lg"}
                      label={text.general.phone}
                      tooltip={text.general.phoneTooltip}
                      placeholder={text.general.phonePlaceholder}
                      inputMode="tel"
                      autoComplete="tel"
                      {...form.getInputProps("phone")}
                      disabled={disabledFields.includes("phone")}
                      customOnChange={(event) => {
                        let digits = (event.target.value || "").replace(
                          /\D/g,
                          ""
                        );
                        if (
                          !(digits.length === 11 && digits.startsWith("1"))
                        ) {
                          digits = digits.substring(0, 10);
                        }
                        const value =
                          digits.length === 11
                            ? digits.replace(
                                /^1(\d{3})(\d{3})(\d{4})$/,
                                "1 $1-$2-$3"
                              )
                            : digits.replace(
                                /(\d{3})(\d{3})(\d{4})/,
                                "$1-$2-$3"
                              );
                        form.getInputProps("phone").onChange(value);
                        form.setFieldValue("phone", value);
                      }}
                    />
                  </Box>
                </Stack>
              )}
            </>
          )}
          {(showOtpScreen || isOtpInput) && (
            <>
              <Center mt={inputSize === "md" ? 25 : 10}>
                <Stack align="center">
                  <ThemeIcon
                    variant="light"
                    c="teal"
                    size={inputSize === "md" ? 70 : 65}
                    radius={"xl"}
                  >
                    <IconShieldCheck
                      stroke={1}
                      size={inputSize === "md" ? 50 : 45}
                      color="teal"
                    />
                  </ThemeIcon>

                  <Text fz={inputSize === "md" ? 30 : 23} fw={700}>
                    {text.phonematch.code.title}
                  </Text>
                  {sendingCode ? (
                    <Group spacing={8} position="center">
                      <Loader size="xs" />
                      <Text c="dimmed" fz={inputSize === "md" ? 24 : 14}>
                        Sending code...
                      </Text>
                    </Group>
                  ) : (
                    <Text
                      ta="center"
                      c="dimmed"
                      fz={inputSize === "md" ? 24 : 14}
                    >
                      {text.phonematch.code.subTitle}
                    </Text>
                  )}
                </Stack>
              </Center>
            </>
          )}
          {/* OTP input always in DOM so iOS keyboard stays open from user gesture pre-focus */}
          <Group
            position="center"
            mt={(showOtpScreen || isOtpInput) ? 40 : 0}
            style={
              (showOtpScreen || isOtpInput)
                ? { opacity: sendingCode ? 0.4 : 1, pointerEvents: sendingCode ? "none" : "auto", filter: sendingCode ? "blur(2px)" : "none", transition: "all 300ms ease" }
                : { position: "absolute", opacity: 0, pointerEvents: "none" }
            }
          >
            <OtpInput
              ref={otpInputRef}
              length={6}
              size={"xl"}
              value={form.values.pin}
              onChange={(val) => form.setFieldValue("pin", val)}
              onComplete={(raw) => submitHandler(raw)}
              error={form.errors.pin}
            />
          </Group>
        </Box>
        {/* pb leaves clearance for the absolute-positioned AppFooter
            ("Powered by Veratad | Privacy Policy") rendered in
            App.js:688 at `bottom: 15`. The footer is ~30px tall, so
            we need at least ~45px of buffer; we use a bit more to
            avoid visual crowding. */}
        <Box pb={70}>
          <Stack spacing={0}>
            {(showOtpScreen || isOtpInput) ? (
              <Stack spacing={0} mt="xl">
                {/* Manual verify button — fallback when OTP
                    auto-submit doesn't fire (autofill, paste edge
                    cases). Only render once all 6 digits are entered
                    so the user doesn't see a phantom "Verifying…"
                    button before they're ready, and so the button
                    isn't visible at all when auto-submit is going
                    to fire on its own. */}
                {(form.values.pin || "").length === 6 && (
                  <Button
                    type="submit"
                    fullWidth
                    size={"lg"}
                    loading={isVerifying || sendingCode}
                    disabled={isVerifying || sendingCode}
                  >
                    {isVerifying ? "Verifying..." : (text.phonematch.code.submit || "Verify")}
                  </Button>
                )}
                <Stack align="center" spacing={0} mt="md">
                  <Text fz="xs" c={theme.colorScheme === "dark" ? theme.colors.gray[4] : theme.colors.dark[6]}>
                    {text.phonematch.code.newCodeNote}
                  </Text>
                  <Button
                    onClick={getNewPin}
                    variant="subtle"
                    styles={{
                      root: {
                        color: theme.colorScheme === "dark" ? theme.colors.gray[3] : theme.colors.dark[6],
                      },
                    }}
                  >
                    {text.phonematch.code.newCodeButton}
                  </Button>
                </Stack>
              </Stack>
            ) : hasAdditionalFields ? (
              activeStep === 0 ? (
                <Button mt="xl" fullWidth size={"lg"} onClick={handleNext}>
                  Next
                </Button>
              ) : (
                <Button
                  mt="xl"
                  fullWidth
                  type="submit"
                  size={"lg"}
                  loading={isVerifying}
                  disabled={isVerifying}
                >
                  {isVerifying ? "Sending code..." : text.phonematch.button}
                </Button>
              )
            ) : (
              <Button
                mt="xl"
                fullWidth
                type="submit"
                size={"lg"}
                loading={isVerifying}
                disabled={isVerifying}
              >
                {isVerifying ? "Sending code..." : text.phonematch.button}
              </Button>
            )}

            {hasAdditionalFields && !showOtpScreen && !isOtpInput && activeStep === 1 && (
              <Button
                variant="subtle"
                color="gray"
                fullWidth
                onClick={() => setActiveStep(0)}
                size="xs"
                compact
                mt={8}
              >
                Back
              </Button>
            )}

            {isSkippable && !showOtpScreen && !isOtpInput && activeStep === 0 && (
              <Button
                variant="subtle"
                color="gray"
                fullWidth
                onClick={handleSkip}
                size="xs"
                compact
                mt={8}
              >
                {currentRoute?.skipableText || "Skip this step"}
              </Button>
            )}
          </Stack>
        </Box>
      </form>
    </>
  );
}
